/**
 * PV-Projekt-Detail-View — vereint KI-Dachanalyse + Briefing + Konfigurator + Angebote.
 *
 * Route: /projects/:id/pv
 *
 * Vorausgesetzt: Mandant hat modules.pv === true.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sun, Battery, Zap, Plus, Trash2, Download, FileBarChart, Loader2,
  Package, Sparkles, Calculator, FileText, Mail, Wand2, Edit3, Send, Clock, X,
  CheckCircle2, XCircle, AlertCircle, ExternalLink, RefreshCw, Scissors,
} from "lucide-react";
import { countModules, exceedsRoof, fitItemsToRoof, toPayloadItems, RoofFitWarning, RoofFitBadge } from "@/components/pv/RoofFit";
import { InverterWarning, InverterBadge } from "@/components/pv/InverterCheck";
import api, { fileUrl } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import AutofillInput from "@/components/AutofillInput";
import RoofAnalysisPanel from "@/components/RoofAnalysisPanel";
import GoogleSolarPanel from "@/components/GoogleSolarPanel";
import RoofLayoutEditor from "@/components/pv/RoofLayoutEditor";
import { TeamCopyNote } from "@/components/TeamCopyNote";
import { DatasheetButton } from "@/components/datasheets/DatasheetButton";
import { usePvDatasheets } from "@/components/datasheets/useDatasheets";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const TYPE_LABELS = { module: "Modul", inverter: "Wechselrichter", battery: "Speicher", wallbox: "Wallbox", addon: "Zusatzleistung" };
const TYPE_ICONS = { module: Sun, inverter: Zap, battery: Battery, wallbox: Plus, addon: Package };
// Iter88zac (2026-02-18) — Nach § 12 Abs. 3 UStG haben PV-Komponenten
// (Module, Wechselrichter, Speicher, PV-Zusatzleistungen) 0 % MwSt. Wallbox
// und alle nicht direkt zur PV gehörenden Positionen behalten 19 %.
// Vertriebler kann pro Position via Dropdown umschalten.
const DEFAULT_VAT_BY_TYPE = { module: 0, inverter: 0, battery: 0, addon: 0, wallbox: 0.19 };
// Einzige Preisquelle für Zeile, Summe und Speichern: aktueller Katalog-VK, Snapshot nur als Fallback (wie Backend).
const cartUnitNet = (it, c) => {
  const catalogUnit = Number(c?.price_eur) || 0;
  return catalogUnit > 0 ? catalogUnit : (Number(it.ref_price_net_eur) || 0);
};
const VAT_OPTIONS = [0, 0.19];
const _eur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 0 });
const _num = (v) => Number(v || 0).toLocaleString("de-DE");

export default function PvProjectDetail() {
  const { id: projectId } = useParams();
  const [catalog, setCatalog] = useState([]);
  const [items, setItems] = useState([]); // [{component_id, qty}]
  const [name, setName] = useState("Standard");
  const [useBattery, setUseBattery] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState([]);
  // Iter89c (2026-02-20) — Liste der bereits erzeugten PV-Angebote, damit
  // der Rep ein bestehendes Angebot direkt in den Konfigurator zurückladen
  // und ein neues Angebot mit neuer Nummer erstellen kann (altes wird
  // Backend-seitig automatisch als `superseded` markiert).
  const [pvOffers, setPvOffers] = useState([]);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [combo, setCombo] = useState(null);
  // Iter88zbh (2026-02-19) — Rabatt-Symmetrie zu WP.
  // `discountMode` toggelt zwischen %-Eingabe und €-Eingabe (nur einer aktiv).
  // Cap-Prüfung passiert Server-seitig; sales_director+ dürfen überschreiben.
  const [discountMode, setDiscountMode] = useState("pct");
  const [discountPct, setDiscountPct] = useState(0);
  const [discountEur, setDiscountEur] = useState(0);
  // Iter88zd (17.02.2026) — Admin-definierte PV-Pakete als Starter (ohne Dachanalyse).
  // Rep wählt ein fertiges Paket → Komponenten landen im Konfigurator → kann
  // ergänzen/löschen/Mengen ändern und dann speichern.
  const [pvPackages, setPvPackages] = useState([]);
  const [pvPackagesLoading, setPvPackagesLoading] = useState(false);

  // Iter58 — Customer-E-Mail (Prefill) + Send-Historie über alle PV-Angebote
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerSachbearbeiterEmail, setCustomerSachbearbeiterEmail] = useState("");
  const [customerSachbearbeiterName, setCustomerSachbearbeiterName] = useState("");
  const [emailSends, setEmailSends] = useState([]);
  const [emailSendsLoading, setEmailSendsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadAll = async () => {
    try {
      const [cat, cfgs, proj, offs] = await Promise.all([
        api.get(`/pv/catalog`),
        api.get(`/projects/${projectId}/pv-configs`),
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/pv-offers`).catch(() => ({ data: { offers: [] } })),
      ]);
      setCatalog(cat.data || []);
      setSavedConfigs(cfgs.data || []);
      setPvOffers(Array.isArray(offs.data?.offers) ? offs.data.offers : []);
      const cust = proj.data?.customer || {};
      setCustomerEmail(cust.email || "");
      setCustomerSachbearbeiterEmail(cust.sachbearbeiter_email || "");
      setCustomerSachbearbeiterName(cust.sachbearbeiter_name || "");
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      toast.error(e.response?.data?.detail || "Konnte PV-Daten nicht laden");
    }
  };

  const loadEmailSends = async () => {
    setEmailSendsLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/pv-email-sends`);
      setEmailSends(Array.isArray(data) ? data : []);
    } catch (e) {
      // Silent — Historie ist Zusatz-Info, kein Blocker
    } finally { setEmailSendsLoading(false); }
  };

  // Iter88zd — PV-Pakete für den Sales-Rep (aktive Pakete des Mandanten inkl.
  // geerbte). Wird beim ersten Mount geladen und nach jedem Save neu gezogen.
  const loadPvPackages = async () => {
    setPvPackagesLoading(true);
    try {
      const { data } = await api.get(`/pv/packages`);
      setPvPackages(Array.isArray(data) ? data : []);
    } catch (e) {
      // Silent — Pakete sind Zusatz, Konfigurator funktioniert auch ohne
    } finally { setPvPackagesLoading(false); }
  };
  useEffect(() => { loadPvPackages(); }, []);
  // Dach-Obergrenze (Google Solar → KI-Dachanalyse) für die Dach-Passt-Prüfung
  const [roofLimit, setRoofLimit] = useState(null);
  const [recalcingId, setRecalcingId] = useState(null);
  const loadRoofLimit = () => api.get(`/projects/${projectId}/pv-roof-limit`).then((r) => setRoofLimit(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRoofLimit(); }, [projectId]);
  const recalcYield = async (cfg) => {
    setRecalcingId(cfg.id);
    try {
      const { data } = await api.post(`/projects/${projectId}/pv-config/${cfg.id}/recalc-yield`);
      setSavedConfigs((list) => list.map((c) => (c.id === cfg.id ? data : c)));
      toast.success(`Ertrag aktualisiert: ${_num(Math.round(data.pvgis?.yearly_kwh || 0))} kWh/Jahr`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ertrag konnte nicht berechnet werden");
    } finally { setRecalcingId(null); }
  };
  const recalcAllYields = async () => {
    setRecalcingId("all");
    try {
      const { data } = await api.post(`/projects/${projectId}/pv-configs/recalc-yield`);
      const byId = Object.fromEntries((data.configs || []).map((c) => [c.id, c]));
      setSavedConfigs((list) => list.map((c) => byId[c.id] || c));
      toast.success(`Ertrag für ${data.updated} Konfiguration${data.updated === 1 ? "" : "en"} aktualisiert`
        + (data.failed ? ` · ${data.failed} fehlgeschlagen` : ""));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erträge konnten nicht berechnet werden");
    } finally { setRecalcingId(null); }
  };
  const fitCartToRoof = () => {
    const before = countModules(items, catalog);
    setItems((cur) => fitItemsToRoof(cur, catalog, roofLimit.max_panels));
    setName((n) => (n || "").replace(`${before}×`, `${roofLimit.max_panels}×`));
    toast.success(`Auf ${roofLimit.max_panels} Module gekürzt – Preis neu berechnet`);
  };
  const [fittingId, setFittingId] = useState(null);
  const fitSavedConfig = async (cfg) => {
    const max = roofLimit.max_panels;
    if (!window.confirm(`„${cfg.name}" auf ${max} Module kürzen?\n\nTeile pro Modul (z. B. Montage) werden mitgekürzt, Preis und Ertrag neu berechnet.`)) return;
    const pct = Number(cfg.discount_percent_requested) || 0;
    const eur = Number(cfg.discount_eur_requested) || 0;
    setFittingId(cfg.id);
    try {
      const { data } = await api.patch(`/projects/${projectId}/pv-config/${cfg.id}`, {
        items: fitItemsToRoof(toPayloadItems(cfg.items), catalog, max),
        name: (cfg.name || "").replace(`${countModules(cfg.items, catalog)}×`, `${max}×`), use_battery: !!cfg.use_battery,
        ...(eur > 0 && pct === 0 ? { discount_percent: 0, discount_eur: eur } : { discount_percent: pct, discount_eur: 0 }),
      });
      toast.success(`„${cfg.name}" auf ${max} Module gekürzt – neuer Preis ${_eur(data.total_price_eur)}`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anpassen fehlgeschlagen");
    } finally { setFittingId(null); }
  };
  useEffect(() => { loadEmailSends(); }, [projectId]);
  // Iter59 — Signatur-Status live aktualisieren wenn User zum Tab zurückkehrt
  // (der Kunde klickt oft in einem anderen Browser-Tab/Handy).
  useEffect(() => {
    const onFocus = () => { loadEmailSends(); };
    const onVis = () => {
      if (document.visibilityState === "visible") loadEmailSends();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [projectId]);

  const seedDemo = async () => {
    try {
      const { data } = await api.post(`/pv/catalog/seed-demo`);
      toast.success(data.skipped ? "Komponenten existieren bereits" : `${data.inserted} Komponenten angelegt`);
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed fehlgeschlagen"); }
  };

  const byType = useMemo(() => {
    const out = { module: [], inverter: [], battery: [], wallbox: [], addon: [] };
    catalog.forEach((c) => { (out[c.type] = out[c.type] || []).push(c); });
    return out;
  }, [catalog]);
  const pvSheets = usePvDatasheets(items, catalog);

  const addItem = (cid) => setItems((prev) => {
    const comp = catalog.find((x) => x.id === cid);
    const defaultVat = DEFAULT_VAT_BY_TYPE[comp?.type] ?? 0;
    const found = prev.find((i) => i.component_id === cid);
    if (found) return prev.map((i) => i.component_id === cid ? { ...i, qty: i.qty + 1 } : i);
    return [...prev, { component_id: cid, qty: 1, vat_rate: defaultVat }];
  });
  const setQty = (cid, qty) => setItems((prev) => prev.map((i) => i.component_id === cid ? { ...i, qty: Math.max(1, qty) } : i).filter((i) => i.qty > 0));
  const setVat = (cid, rate) => setItems((prev) => prev.map((i) => i.component_id === cid ? { ...i, vat_rate: rate } : i));
  const removeItem = (cid) => setItems((prev) => prev.filter((i) => i.component_id !== cid));

  // Iter88zbg (2026-02-19) — Freie Positionen: Rep tippt Bezeichnung + Preis
  // frei ein (analog zu WP `addFreeLine`). Wird ohne Marge übernommen — der
  // Rep hat den Endpreis bereits final gewählt. Frontend-Identität über
  // `_local_id`, das Backend ignoriert dieses Feld beim Speichern.
  const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
  const addFreeLine = () => setItems((prev) => [...prev, {
    _local_id: uid(),
    is_free: true,
    title: "",
    qty: 1,
    unit_price_net_eur: 0,
    vat_rate: 0,
  }]);
  const updateFreeLine = (localId, patch) => setItems((prev) => prev.map((i) => i._local_id === localId ? { ...i, ...patch } : i));
  const removeFreeLine = (localId) => setItems((prev) => prev.filter((i) => i._local_id !== localId));

  const liveTotals = useMemo(() => {
    const lookup = Object.fromEntries(catalog.map((c) => [c.id, c]));
    let kwp = 0, net = 0, gross = 0;
    items.forEach((it) => {
      // Iter88zbg — Freie Position: Preis kommt direkt vom Rep-Feld.
      if (it.is_free) {
        const unit = Number(it.unit_price_net_eur) || 0;
        const qty = Number(it.qty) || 0;
        const vat = Number(it.vat_rate) || 0;
        const lineNet = unit * qty;
        net += lineNet;
        gross += lineNet * (1 + vat);
        return;
      }
      const { component_id, qty, vat_rate } = it;
      const c = lookup[component_id]; if (!c) return;
      const lineNet = cartUnitNet(it, c) * qty;
      const vat = typeof vat_rate === "number" ? vat_rate : (DEFAULT_VAT_BY_TYPE[c.type] ?? 0);
      net += lineNet;
      gross += lineNet * (1 + vat);
      if (c.type === "module") kwp += (c.kwp_or_kw || 0) * qty;
    });
    // Iter88zbh — Rabatt anwenden (Frontend-Preview, Server rechnet nochmal
    // mit Cap-Prüfung nach). Wir zeigen den REQUESTED Rabatt, damit der Rep
    // sofort sieht was er tippt — Cap-Info kommt nach dem Speichern zurück.
    const discountRawEur = discountMode === "eur"
      ? Math.min(Math.max(0, Number(discountEur) || 0), net)
      : net * (Math.max(0, Math.min(100, Number(discountPct) || 0)) / 100);
    const discountEurRounded = Math.round(discountRawEur * 100) / 100;
    const netAfter = Math.max(0, net - discountEurRounded);
    // Rabatt-Faktor auf gross proportional anwenden (damit MwSt-Split
    // pro Zeile korrekt bleibt, identisch zu Backend `_apply_pv_discount`).
    const factor = net > 0.005 ? netAfter / net : 1;
    const grossAfter = gross * factor;
    return {
      kwp: Math.round(kwp * 100) / 100,
      // Iter88zbg — 2 Nachkommastellen (identisch zur persistierten Konfig).
      subtotalNet: Math.round(net * 100) / 100,
      subtotalGross: Math.round(gross * 100) / 100,
      discountEur: discountEurRounded,
      price: Math.round(grossAfter * 100) / 100,
      net: Math.round(netAfter * 100) / 100,
      vatDelta: Math.round((grossAfter - netAfter) * 100) / 100,
    };
  }, [items, catalog, discountMode, discountPct, discountEur]);

  const saveConfig = async () => {
    if (items.length === 0) return toast.error("Bitte mindestens eine Komponente wählen");
    // Iter88zbg — `_local_id` ist reines Frontend-Handle, nicht ans Backend schicken.
    const payloadItems = items.map(({ _local_id, ...rest }) => rest);
    // Iter88zbh — Rabatt mitschicken (Backend prüft Cap + überschreibt bei
    // sales_director+). `discountMode` steuert welches Feld gefüllt wird —
    // das andere bleibt 0, damit Backend nur einen Rabatt anwendet.
    const discountPayload = discountMode === "eur"
      ? { discount_percent: 0, discount_eur: Number(discountEur) || 0 }
      : { discount_percent: Number(discountPct) || 0, discount_eur: 0 };
    setSaving(true);
    try {
      let resp;
      if (editingConfigId) {
        // Iter98 — bestehende Konfiguration erweitern/ändern
        resp = await api.patch(`/projects/${projectId}/pv-config/${editingConfigId}`,
          { items: payloadItems, name, use_battery: useBattery, ...discountPayload });
        toast.success(`„${name}" aktualisiert`);
      } else {
        resp = await api.post(`/projects/${projectId}/pv-config`,
          { items: payloadItems, name, use_battery: useBattery, ...discountPayload });
        toast.success(`Konfiguration „${name}" gespeichert`);
      }
      // Iter88zbh — Cap-Warnung wenn der Backend-Cap gegriffen hat.
      if (resp?.data?.discount_was_capped) {
        toast.warning(
          `Rabatt gekappt: ${_eur(resp.data.discount_eur_applied)} statt gewünschten ` +
          `${_eur(resp.data.discount_eur_requested || (resp.data.subtotal_net * (resp.data.discount_percent_requested || 0) / 100))} — ` +
          `bitte Vertriebsleitung einbeziehen.`
        );
      }
      setItems([]);
      setEditingConfigId(null);
      setDiscountPct(0);
      setDiscountEur(0);
      setDiscountMode("pct");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const downloadOffer = async (cfgId, cfgName) => {
    try {
      const r = await api.get(`/projects/${projectId}/pv-offer.pdf?config_id=${cfgId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `pv-angebot_${cfgName}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Angebot heruntergeladen");
    } catch (e) { toast.error(e.response?.data?.detail || "Download fehlgeschlagen"); }
  };

  // Iter88zbf (2026-02-19) — Konfiguration wieder löschen können. Backend-
  // Endpoint existiert bereits; hier nur die UI-Wiring + Bestätigungs-Dialog.
  const deleteConfig = async (cfg) => {
    if (!window.confirm(
      `Konfiguration „${cfg.name}" wirklich löschen?\n\n` +
      `${cfg.total_kwp} kWp · ${_eur(cfg.total_price_eur)}\n\n` +
      `Verknüpfte Angebote bleiben bestehen; nur der Konfig-Snapshot wird entfernt.`
    )) return;
    try {
      await api.delete(`/projects/${projectId}/pv-config/${cfg.id}`);
      toast.success(`„${cfg.name}" gelöscht`);
      if (editingConfigId === cfg.id) {
        setEditingConfigId(null);
        setItems([]);
      }
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  // Iter57/58 — E-Mail-Vorschau-Dialog (statt Browser-prompt/confirm)
  const [emailDialog, setEmailDialog] = useState({
    open: false, cfgId: null, cfgName: "", to: "", cc: "", message: "", sending: false, templateId: "empty",
  });
  const [emailTemplates, setEmailTemplates] = useState([]);
  useEffect(() => {
    api.get("/email-templates").then((r) => setEmailTemplates(r.data?.templates || [])).catch(() => {});
  }, []);

  const applyTemplate = (tplId) => {
    const tpl = emailTemplates.find((t) => t.id === tplId);
    if (!tpl) return;
    // Iter88t: Placeholder ersetzen — Kunden-Name als Anrede.
    let body = tpl.body || "";
    const kunde = [emailDialog.to && ""].join("") || "geehrte Damen und Herren";
    body = body.replace(/\{kunde\}/g, kunde)
               .replace(/\{angebotsdatum\}/g, new Date().toLocaleDateString("de-DE"))
               .replace(/\{angebotsnummer\}/g, emailDialog.cfgName || "")
               .replace(/\{termin_datum\}/g, "")
               .replace(/\{telefon\}/g, "");
    setEmailDialog((d) => ({ ...d, templateId: tplId, message: body }));
  };

  const openEmailDialog = (cfg) => {
    setEmailDialog({
      open: true,
      cfgId: cfg.id,
      cfgName: cfg.name || "PV-Angebot",
      to: customerEmail || "",
      cc: customerSachbearbeiterEmail || "",
      message: "",
      templateId: "empty",
      sending: false,
    });
  };

  const closeEmailDialog = () => {
    if (emailDialog.sending) return;
    setEmailDialog({ open: false, cfgId: null, cfgName: "", to: "", cc: "", message: "", templateId: "empty", sending: false });
  };

  // Iter57 — Kombinierter Flow: erst Formal-Angebot erzeugen (mit QR-Sign-Link),
  // dann PDF-Anhang + Signing-Link per E-Mail an Kunde senden.
  const submitEmailDialog = async () => {
    const trimmed = (emailDialog.to || "").trim();
    if (!trimmed) {
      toast.error("Bitte Empfänger-E-Mail eintragen");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Ungültige E-Mail-Adresse");
      return;
    }
    setEmailDialog((d) => ({ ...d, sending: true }));
    const tid = toast.loading("Angebot wird erstellt und gesendet…");
    try {
      const r = await api.post(`/projects/${projectId}/pv-offers`, { config_id: emailDialog.cfgId });
      const off = r.data?.offer;
      if (!off?.id) throw new Error("Angebot konnte nicht erstellt werden");
      const s = await api.post(`/offers/${off.id}/send-email`, {
        to: trimmed,
        cc: (emailDialog.cc || "").trim() || undefined,
        custom_message: (emailDialog.message || "").trim() || null,
      });
      toast.dismiss(tid);
      const ccList = s.data?.cc || [];
      toast.success(
        ccList.length > 0
          ? `Angebot ${off.offer_number} an ${s.data?.to} gesendet (CC: ${ccList.join(", ")})`
          : `Angebot ${off.offer_number} an ${s.data?.to} gesendet`
      );
      setEmailDialog({ open: false, cfgId: null, cfgName: "", to: "", cc: "", message: "", sending: false });
      loadEmailSends();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e.response?.data?.detail || e.message || "E-Mail-Versand fehlgeschlagen");
      setEmailDialog((d) => ({ ...d, sending: false }));
    }
  };

  const sendOffer = (cfgId, cfgName = "Angebot") => {
    const cfg = savedConfigs.find((c) => c.id === cfgId) || { id: cfgId, name: cfgName };
    openEmailDialog(cfg);
  };

  const [batteryRec, setBatteryRec] = useState(null);
  // Iteration 98 — Editier-Modus: bestehende Konfiguration in den Konfigurator
  // laden, Komponenten hinzufügen/entfernen/Menge ändern, und beim Speichern
  // die bestehende Konfig updaten statt eine neue anzulegen.
  const [editingConfigId, setEditingConfigId] = useState(null);

  const loadIntoConfigurator = (cfg) => {
    setItems((cfg.items || []).map((i) => {
      // Iter88zbg — Freie Positionen behalten Titel + Preis 1:1.
      if (i.is_free) {
        return {
          _local_id: uid(),
          is_free: true,
          title: i.title || "",
          qty: i.qty || 1,
          unit_price_net_eur: Number(i.unit_price_net_eur) || 0,
          vat_rate: typeof i.vat_rate === "number" ? i.vat_rate : 0,
        };
      }
      return {
        component_id: i.component_id,
        qty: i.qty || 1,
        // Iter88zad — Paket-Einzelpreise (`ref_price_net_eur`) UND MwSt-Satz
        // erhalten, damit die Konfig-Summe beim Speichern nicht auf den
        // Katalog-Listenpreis explodiert (Bug: 12.792€ → 18.981€ Diff).
        ...(typeof i.ref_price_net_eur === "number" && i.ref_price_net_eur > 0
          ? { ref_price_net_eur: i.ref_price_net_eur } : {}),
        ...(typeof i.vat_rate === "number" ? { vat_rate: i.vat_rate } : {}),
      };
    }));
    setName(cfg.name || "Standard");
    setUseBattery(!!cfg.use_battery);
    setEditingConfigId(cfg.id);
    // Iter88zbh — Rabatt beim Laden rehydrieren (%-Modus hat Vorrang wenn beides > 0).
    const pct = Number(cfg.discount_percent_requested) || 0;
    const eur = Number(cfg.discount_eur_requested) || 0;
    if (eur > 0 && pct === 0) {
      setDiscountMode("eur");
      setDiscountEur(eur);
      setDiscountPct(0);
    } else {
      setDiscountMode("pct");
      setDiscountPct(pct);
      setDiscountEur(0);
    }
    // Zum Konfigurator scrollen
    setTimeout(() => {
      document.querySelector('[data-testid="pv-configurator"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    toast.success(`„${cfg.name}" zum Bearbeiten geladen — Komponenten anpassen und speichern`);
  };

  const cancelEditing = () => {
    setEditingConfigId(null);
    setEditingOfferId(null);
    setItems([]);
    setName("Standard");
    setUseBattery(false);
    setDiscountPct(0);
    setDiscountEur(0);
    setDiscountMode("pct");
  };

  // Iter89c (2026-02-20) — Bestehendes PV-Angebot in den Konfigurator laden.
  // Analog `loadIntoConfigurator(cfg)`, aber die Datenquelle sind
  // `offer.items` aus einem `offers`-Dokument (offer_type="pv"). Der Rep
  // kann anschließend Positionen hinzufügen/entfernen/Mengen ändern und
  // via „Als neues Angebot speichern" einen frischen Angebots-Datensatz mit
  // neuer Nummer erzeugen — das alte Angebot wird Backend-seitig automatisch
  // auf `status: superseded` gesetzt (siehe `pv_offers.py:271-280`).
  const loadOfferIntoConfigurator = (offer) => {
    const rawItems = Array.isArray(offer.items) ? offer.items : [];
    const newItems = rawItems.map((i) => {
      // Freie Position: Titel + Netto-Preis 1:1 in die Konfigurator-Form.
      if (i.is_free) {
        return {
          _local_id: uid(),
          is_free: true,
          title: i.title || "",
          qty: Number(i.quantity) || 1,
          unit_price_net_eur: Number(i.unit_price_net) || 0,
          vat_rate: typeof i.vat_rate === "number" ? i.vat_rate : 0,
        };
      }
      // Katalog-Position: den beim Angebot eingefrorenen Netto-Preis als
      // `ref_price_net_eur` mitgeben, damit die neue Konfig-Summe nicht auf
      // aktuelle Katalog-Preise „hochspringt" (identisch zum Konfig-Flow).
      return {
        component_id: i.component_id,
        qty: Number(i.quantity) || 1,
        ref_price_net_eur: Number(i.unit_price_net) || 0,
        vat_rate: typeof i.vat_rate === "number" ? i.vat_rate : 0,
      };
    });
    setItems(newItems);
    setName(`Angebot ${offer.offer_number || ""}`.trim());
    setUseBattery(rawItems.some((i) => (i.type || "").toLowerCase() === "battery"));
    setEditingOfferId(offer.id);
    setEditingConfigId(null);
    // Rabatt-Modus rehydrieren
    const pct = Number(offer.discount_percent_requested) || 0;
    const eur = Number(offer.discount_eur_requested) || 0;
    if (eur > 0 && pct === 0) {
      setDiscountMode("eur");
      setDiscountEur(eur);
      setDiscountPct(0);
    } else {
      setDiscountMode("pct");
      setDiscountPct(pct);
      setDiscountEur(0);
    }
    setTimeout(() => {
      document
        .querySelector('[data-testid="pv-configurator"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    toast.success(
      `Angebot ${offer.offer_number} zum Bearbeiten geladen — nach dem Speichern entsteht ein neues Angebot; das alte wird als abgelöst markiert.`
    );
  };

  // Iter89c (2026-02-20) — „Als neues Angebot speichern" im Offer-Edit-Modus.
  // Sendet die aktuellen Konfigurator-Positionen direkt an POST /pv-offers
  // (ohne Umweg über eine gespeicherte Konfig). Backend rechnet Marge +
  // Rabatt-Cap identisch zum Konfig-Pfad, markiert alte PV-Angebote als
  // superseded und erzeugt eine neue Angebotsnummer.
  const [creatingOffer, setCreatingOffer] = useState(false);
  const saveAsNewPvOffer = async () => {
    if (items.length === 0) {
      toast.error("Bitte mindestens eine Komponente wählen.");
      return;
    }
    // Konfigurator-Items in das Payload-Format bringen, das der POST-Handler
    // ohne `config_id` erwartet (siehe pv_offers.py:80–150).
    const lookup = Object.fromEntries((catalog || []).map((c) => [c.id, c]));
    const payloadItems = items.map((it) => {
      if (it.is_free) {
        return {
          component_id: null,
          title: (it.title || "Freie Position").trim(),
          quantity: Number(it.qty) || 1,
          unit_price_net: Number(it.unit_price_net_eur) || 0,
          wholesale_net: Number(it.unit_price_net_eur) || 0,
          vat_rate: Number(it.vat_rate) || 0,
          type: "free",
          is_free: true,
        };
      }
      const c = lookup[it.component_id] || {};
      const unit = cartUnitNet(it, c);
      const vat = typeof it.vat_rate === "number" ? it.vat_rate : (DEFAULT_VAT_BY_TYPE[c.type] ?? 0);
      return {
        component_id: it.component_id,
        title: `${c.brand || ""} ${c.model || ""}`.trim() || "Position",
        quantity: Number(it.qty) || 1,
        unit_price_net: unit,
        wholesale_net: Number(c.price_eur) || unit,
        vat_rate: Number(vat) || 0,
        type: c.type || null,
      };
    });
    const total_kwp = items.reduce((sum, it) => {
      if (it.is_free) return sum;
      const c = lookup[it.component_id];
      if (c?.type === "module") return sum + (Number(c.kwp_or_kw) || 0) * (Number(it.qty) || 0);
      return sum;
    }, 0);
    const discountPayload = discountMode === "eur"
      ? { discount_percent: 0, discount_eur: Number(discountEur) || 0 }
      : { discount_percent: Number(discountPct) || 0, discount_eur: 0 };
    setCreatingOffer(true);
    try {
      const r = await api.post(`/projects/${projectId}/pv-offers`, {
        items: payloadItems,
        total_kwp: Number(total_kwp.toFixed(2)),
        ...discountPayload,
      });
      const off = r.data?.offer;
      if (off?.discount_was_capped) {
        toast.warning("Rabatt wurde auf das Maximum begrenzt.");
      }
      toast.success(
        `Neues PV-Angebot ${off?.offer_number} erstellt — vorherige Version wurde abgelöst.`
      );
      setEditingOfferId(null);
      setItems([]);
      setName("Standard");
      setDiscountPct(0);
      setDiscountEur(0);
      setDiscountMode("pct");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Angebots-Erstellung fehlgeschlagen");
    } finally {
      setCreatingOffer(false);
    }
  };

  // Auto-Redirect nach „Als PV-Paket übernehmen": die vom Backend gepickten
  // Komponenten direkt in den Projekt-Konfigurator laden, sodass der
  // Vertriebler ohne Zusatzklick weiterarbeiten (anpassen + speichern) kann.
  const handlePackageCreated = (resp) => {
    const pkg = resp?.package;
    const pkgItems = Array.isArray(pkg?.items) ? pkg.items : [];
    if (pkgItems.length === 0) return;
    // Iter88zad — Paket-Einzelpreise (`ref_price_net_eur`) übernehmen,
    // damit der Konfig-Preis beim Speichern gleich dem Paket-Preis bleibt.
    // Sonst rechnet Backend aus Katalog-Listenpreisen → große Diskrepanz.
    const lookup = Object.fromEntries((catalog || []).map((c) => [c.id, c]));
    const newItems = pkgItems.map((it) => {
      const comp = lookup[it.component_id];
      const out = {
        component_id: it.component_id,
        qty: Math.max(1, Math.round(Number(it.qty) || 1)),
      };
      const ref = Number(it.ref_price_net_eur);
      if (ref > 0) out.ref_price_net_eur = ref;
      // Default-MwSt aus Typ (Wallbox 19%, Rest 0%)
      out.vat_rate = DEFAULT_VAT_BY_TYPE[comp?.type] ?? 0;
      return out;
    });
    const hasBattery = pkgItems.some((it) => lookup[it.component_id]?.type === "battery");
    setItems(newItems);
    setName(pkg.name || "PV-Paket");
    setUseBattery(hasBattery);
    setEditingConfigId(null); // neu erstellte Konfiguration, kein Edit-Mode
    setTimeout(() => {
      document
        .querySelector('[data-testid="pv-configurator"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    toast.success(
      `Paket „${pkg.name}" in Konfigurator übernommen — Komponenten anpassen und speichern`
    );
  };

  const createPvOffer = async (cfg) => {
    if (!window.confirm(
      `PV-Angebot aus „${cfg.name}" erzeugen?\n\n` +
      `Es wird ein rechtsverbindliches Angebot mit AGB, Widerrufsbelehrung ` +
      `und QR-Signatur erstellt (Umsatzsteuer gemäß Konfiguration/Kundentyp).`
    )) return;
    // WICHTIG: Popup-Blocker verhindern window.open() nach async-Kette.
    // Wir öffnen den Tab SOFORT synchron im User-Gesture-Kontext und
    // navigieren später zur PDF-URL, sobald sie fertig ist.
    const preTab = window.open("", "_blank", "noopener,noreferrer");
    if (preTab) {
      preTab.document.write(
        '<title>PV-Angebot wird erzeugt…</title>' +
        '<body style="font-family:system-ui;background:#09090B;color:#fff;' +
        'display:flex;align-items:center;justify-content:center;height:100vh;margin:0;">' +
        '<div style="text-align:center"><div style="font-size:14px;letter-spacing:2px;' +
        'text-transform:uppercase;color:#0EA5E9;font-weight:700">Novatus</div>' +
        '<div style="font-size:18px;margin-top:8px">PV-Angebot wird erzeugt…</div>' +
        '<div style="font-size:12px;color:#71717a;margin-top:4px">' +
        'Bitte kurz warten – die PDF öffnet sich hier automatisch.</div></div></body>'
      );
    }
    try {
      const r = await api.post(`/projects/${projectId}/pv-offers`, { config_id: cfg.id });
      const off = r.data?.offer;
      toast.success(`PV-Angebot ${off?.offer_number} erstellt`);
      const rp = await api.get(`/offers/${off.id}/pv-pdf`);
      if (rp.data?.pdf_storage_path) {
        const url = fileUrl(rp.data.pdf_storage_path);
        toast.success("PDF fertig — öffnet sich im Tab");
        if (preTab && !preTab.closed) {
          preTab.location.href = url;
        } else {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } else if (preTab && !preTab.closed) {
        preTab.close();
      }
      // Iter89c — Liste nachladen, damit das gerade erzeugte Angebot in der
      // neuen „PV-Angebote"-Sektion sofort sichtbar wird.
      await loadAll();
    } catch (e) {
      if (preTab && !preTab.closed) preTab.close();
      toast.error(e.response?.data?.detail || "Angebot-Erstellung fehlgeschlagen");
    }
  };
  const optimizeBattery = async (cfgId) => {
    try {
      const { data } = await api.get(`/projects/${projectId}/pv-config/${cfgId}/optimize-battery`);
      setBatteryRec({ ...data, config_id: cfgId });
    } catch (e) { toast.error(e.response?.data?.detail || "Optimierung fehlgeschlagen"); }
  };

  const [compareSelected, setCompareSelected] = useState([]);
  const toggleCompare = (id) => setCompareSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-3));

  const downloadCompare = async () => {
    if (compareSelected.length < 2) return toast.error("Bitte 2-3 Konfigurationen auswählen");
    try {
      const r = await api.get(`/projects/${projectId}/pv-compare.pdf?config_ids=${compareSelected.join(",")}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `pv-vergleich.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Vergleich heruntergeladen");
    } catch (e) { toast.error(e.response?.data?.detail || "Download fehlgeschlagen"); }
  };

  const loadCombo = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/combo-economics`);
      setCombo(data);
    } catch (e) { /* combo optional */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (savedConfigs.length > 0) loadCombo(); }, [savedConfigs]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#09090B]" data-testid="pv-project-not-found">
        <AppHeader title="PV-Projekt" back="/dashboard" />
        <main className="max-w-xl mx-auto px-4 pt-16 text-center space-y-4">
          <p className="text-lg font-semibold text-zinc-100">Projekt nicht gefunden</p>
          <p className="text-sm text-zinc-400">Dieses Projekt existiert nicht oder ist für dich nicht freigegeben.</p>
          <Link to="/dashboard" data-testid="pv-project-not-found-back" className="inline-flex h-11 items-center px-5 rounded-xl bg-[#00E575] text-[#09090B] font-bold transition-transform active:scale-[0.98]">Zur Übersicht</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="pv-project-detail">
      <AppHeader title="PV-Projekt" back={`/projects/${projectId}`} />
      <main className="ios-no-zoom max-w-5xl mx-auto px-3 sm:px-5 pt-5 pb-28 sm:pb-5 space-y-5">

        {/* 1) KI-Dachanalyse */}
        <RoofAnalysisPanel projectId={projectId} onChanged={loadRoofLimit} onAccept={(kwp) => toast.info(`KI-Vorschlag: ${kwp} kWp — Module passend dazu wählen`)} />

        {/* 1b) Google Solar-Dachanalyse (offizielle Google-Daten + Auto-Paket-Vorschläge) */}
        <GoogleSolarPanel projectId={projectId} onPackageCreated={handlePackageCreated} onChanged={loadRoofLimit} />

        {/* 1c) Iter88zd — PV-Paket-Picker (ohne Dachanalyse verfügbar)
              Der Rep wählt ein vom Admin definiertes PV-Paket → Komponenten
              werden 1:1 in den Konfigurator geladen und können anschließend
              ergänzt / gelöscht / in der Menge angepasst werden. */}
        {pvPackages.length > 0 && (
          <section className="bg-gradient-to-br from-[#00E575]/5 to-sky-500/5 border-2 border-[#00E575]/30 rounded-2xl p-5 space-y-4" data-testid="pv-package-picker">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="font-display text-lg font-bold inline-flex items-center gap-2">
                  <Package size={18} className="text-[#00E575]" />
                  PV-Paket wählen
                </h2>
                <p className="text-[12px] text-zinc-400 mt-0.5">
                  Fertiges Paket als Ausgangsbasis wählen — im Konfigurator unten kannst du danach Komponenten ergänzen oder entfernen.
                </p>
              </div>
              {pvPackagesLoading && <Loader2 size={16} className="animate-spin text-zinc-400" />}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {pvPackages.map((pkg) => {
                const itemCount = (pkg.items || []).length;
                const hasBattery = (pkg.items || []).some((it) => {
                  const c = catalog.find((x) => x.id === it.component_id);
                  return c?.type === "battery";
                });
                return (
                  <button
                    key={pkg.id}
                    onClick={() => handlePackageCreated({ package: pkg })}
                    data-testid={`pv-package-pick-${pkg.id}`}
                    className="text-left bg-[#0F0F11] border-2 border-zinc-800 hover:border-[#00E575] rounded-xl p-4 transition group focus:outline-none focus:ring-2 focus:ring-[#00E575]/50"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-bold text-zinc-100 truncate">{pkg.name}</div>
                        {pkg.description && (
                          <div className="text-[11.5px] text-zinc-400 line-clamp-2 mt-0.5">{pkg.description}</div>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="font-display text-[16px] font-black text-[#00E575]">
                          {_eur(pkg.price_eur_gross || pkg.price_eur)}
                        </div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider" title="Netto = Brutto · § 12 Abs. 3 UStG">netto = brutto</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[11.5px] text-zinc-300 pt-2 border-t border-zinc-800">
                      <span className="inline-flex items-center gap-1">
                        <Sun size={12} className="text-amber-400" />
                        {Number(pkg.target_kwp || 0).toLocaleString("de-DE", { maximumFractionDigits: 1 })} kWp
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Package size={12} className="text-sky-400" />
                        {itemCount} Komponenten
                      </span>
                      {hasBattery && (
                        <span className="inline-flex items-center gap-1 text-emerald-300">
                          <Battery size={12} /> Speicher
                        </span>
                      )}
                    </div>
                    <div className="mt-2"><RoofFitBadge modules={countModules(pkg.items, catalog)} limit={roofLimit} testid={`pv-package-roof-warn-${pkg.id}`} /></div>
                    <div className="mt-2.5 text-[11px] font-bold text-[#00E575] opacity-0 group-hover:opacity-100 transition">
                      → In Konfigurator laden
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Info-Card wenn keine PV-Pakete verfügbar — Rep bekommt klare Handlungsanweisung */}
        {!pvPackagesLoading && pvPackages.length === 0 && (
          <section className="bg-amber-500/5 border border-amber-500/30 rounded-2xl p-4 flex items-start gap-3" data-testid="pv-package-picker-empty">
            <AlertCircle size={18} className="text-amber-400 mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-amber-100">
              <b>Noch keine PV-Pakete für diesen Mandanten hinterlegt.</b>
              <p className="mt-1 text-zinc-300 text-[12px]">
                Du kannst trotzdem direkt im Konfigurator unten Komponenten einzeln auswählen und speichern. Alternativ kann ein Admin unter{" "}
                <Link to="/admin/pv-packages" className="underline text-amber-200 hover:text-amber-100 font-semibold">
                  Admin → PV-Pakete
                </Link>{" "}
                Basis-Pakete (z. B. &bdquo;Basic 8 kWp&ldquo;, &bdquo;Komfort 10 kWp&ldquo;) anlegen — dann erscheinen sie hier als 1-Klick-Auswahl.
              </p>
            </div>
          </section>
        )}

        {/* 2) Konfigurator */}
        <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5 space-y-4" data-testid="pv-configurator">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><Calculator size={18} className="text-[#00E575]"/> Konfigurator</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {catalog.length > 0 && (
                <button onClick={addFreeLine} data-testid="pv-add-free-line-top"
                  className="h-9 px-3 rounded-lg bg-[#0F0F11] border border-dashed border-zinc-500 text-zinc-300 hover:border-[#00E575] hover:text-[#00E575] text-[11.5px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5">
                  <Plus size={13}/> Freie Position
                </button>
              )}
              {catalog.length === 0 && (
                <button onClick={seedDemo} data-testid="pv-seed-demo" className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[11.5px] inline-flex items-center gap-1.5 hover:bg-[#00C964]">
                  <Sparkles size={13}/> Beispiel-Komponenten laden
                </button>
              )}
            </div>
          </div>

          {/* Iter98 — Editier-Modus-Banner (Konfig) */}
          {editingConfigId && !editingOfferId && (
            <div className="rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-2.5 flex items-center gap-2 flex-wrap" data-testid="pv-edit-mode-banner">
              <Edit3 size={14} className="text-sky-300 flex-shrink-0"/>
              <div className="text-[12.5px] text-sky-100 font-medium min-w-0 flex-1">
                <b>Bearbeitungsmodus</b> — du erweiterst gerade &bdquo;{name}&ldquo;. Komponenten hinzufügen, entfernen oder Mengen ändern und dann speichern.
              </div>
              <button onClick={cancelEditing} data-testid="pv-cancel-edit"
                className="text-[12px] text-sky-200 hover:text-white font-semibold px-2 py-1 rounded hover:bg-sky-500/20">
                Abbrechen
              </button>
            </div>
          )}

          {/* Iter89c (2026-02-20) — Editier-Modus-Banner (Angebot). Deutlich
              hervorgehoben (amber) da nach dem Speichern ein NEUES Angebot
              mit neuer Nummer entsteht und das alte abgelöst wird.
              sticky top-[76px] hält den Banner sichtbar, aber unter dem
              fixierten AppHeader — Testing-Agent-Hinweis Iter89c. */}
          {editingOfferId && (() => {
            const off = pvOffers.find((o) => o.id === editingOfferId);
            return (
              <div className="sticky top-2 xl:top-[76px] z-20 rounded-xl border border-amber-500/50 bg-amber-500/10 backdrop-blur-md px-4 py-3 flex items-start gap-3 flex-wrap shadow-lg" data-testid="pv-edit-offer-banner">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Edit3 size={16} className="text-amber-300"/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-extrabold text-amber-200">
                    PV-Angebot bearbeiten — {off?.offer_number || "…"}
                  </div>
                  <p className="text-[12px] text-amber-100/85 leading-snug mt-0.5">
                    Positionen, Mengen und Rabatt anpassen. Beim <b className="text-amber-100">Speichern</b> entsteht ein neues Angebot mit neuer Nummer; das aktuelle wird als <b className="text-amber-100">abgelöst</b> markiert und der QR-Code gesperrt.
                  </p>
                </div>
                <button onClick={cancelEditing} data-testid="pv-cancel-edit-offer"
                  className="text-[12px] text-amber-100 hover:text-white font-semibold px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40">
                  Abbrechen
                </button>
              </div>
            );
          })()}

          {catalog.length === 0 ? (
            <div className="text-zinc-500 text-sm py-4 text-center">Noch keine Komponenten vorhanden. &bdquo;Beispiel-Komponenten laden&ldquo; klicken.</div>
          ) : (
            <>
              {["module", "inverter", "battery", "wallbox", "addon"].map((t) => {
                const Icon = TYPE_ICONS[t];
                const catItems = byType[t] || [];
                if (catItems.length === 0) return null;
                return (
                  <div key={t} data-testid={`pv-cat-${t}`}>
                    <h3 className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-2 inline-flex items-center gap-1"><Icon size={11}/> {TYPE_LABELS[t]}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {catItems.map((c) => (
                        <button key={c.id} onClick={() => addItem(c.id)} data-testid={`pv-add-${c.id}`}
                          className="text-left bg-[#0F0F11] border border-zinc-800 hover:border-[#00E575] rounded-xl p-3 transition">
                          <div className="flex justify-between items-baseline gap-2">
                            <div className="min-w-0">
                              <div className="text-[12px] font-bold truncate">{c.brand}</div>
                              <div className="text-[12px] text-zinc-400 truncate">{c.model}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-mono text-[13px] font-bold">{_eur(c.price_eur)}</div>
                              {t !== "addon" && (
                                <div className="text-[12px] text-zinc-300">{c.kwp_or_kw} {t === "battery" ? "kWh" : "kW"}{t === "module" ? "p" : ""}</div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Live Cart */}
              {items.length > 0 && (
                <div className="bg-[#0F0F11] border border-[#00E575]/40 rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-2" data-testid="pv-cart">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h3 className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold">Aktuelle Auswahl</h3>
                    <DatasheetButton sheets={pvSheets.datasheets} missing={pvSheets.missing} testid="pv-datasheet-btn" />
                  </div>
                  {items.map((it, idx) => {
                    // Iter88zbg — Freie Position: eigene Render-Zeile mit Text-Input
                    if (it.is_free) {
                      const unit = Number(it.unit_price_net_eur) || 0;
                      const qty = Number(it.qty) || 0;
                      const vat = Number(it.vat_rate) || 0;
                      const lineGross = unit * qty * (1 + vat);
                      return (
                        <div key={it._local_id || `free-${idx}`} className="flex flex-wrap sm:flex-nowrap items-center gap-2 text-[12.5px] pb-3 sm:pb-0 border-b border-zinc-800/70 sm:border-0" data-testid={`pv-free-line-${it._local_id}`}>
                          <input
                            type="text"
                            value={it.title || ""}
                            onChange={(e) => updateFreeLine(it._local_id, { title: e.target.value })}
                            placeholder="Bezeichnung (z. B. Gerüst-Aufbau, Sonderleistung)…"
                            data-testid={`pv-free-title-${it._local_id}`}
                            className="basis-[calc(100%-3rem)] sm:basis-auto flex-1 min-w-0 h-9 sm:h-7 bg-zinc-800 border border-zinc-700 rounded px-2 text-[12.5px] focus:border-[#00E575] outline-none"
                          />
                          <button onClick={() => removeFreeLine(it._local_id)} data-testid={`pv-free-del-${it._local_id}`} className="sm:order-last w-10 h-10 sm:w-auto sm:h-auto inline-flex items-center justify-center rounded-lg sm:rounded-none bg-rose-500/10 sm:bg-transparent text-rose-300 hover:text-rose-200"><Trash2 size={15}/></button>
                          <input
                            type="number" min="1" step="1" value={qty}
                            onChange={(e) => updateFreeLine(it._local_id, { qty: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                            className="w-14 h-9 sm:h-7 bg-zinc-800 border border-zinc-700 rounded px-2 text-center"
                            data-testid={`pv-free-qty-${it._local_id}`}
                            title="Menge"
                          />
                          <input
                            type="number" min="0" step="0.01" value={unit}
                            onChange={(e) => updateFreeLine(it._local_id, { unit_price_net_eur: parseFloat(e.target.value) || 0 })}
                            className="w-20 sm:w-24 h-9 sm:h-7 bg-zinc-800 border border-zinc-700 rounded px-2 text-right font-mono"
                            data-testid={`pv-free-price-${it._local_id}`}
                            title="Netto-Einzelpreis"
                          />
                          <select
                            value={String(vat)}
                            onChange={(e) => updateFreeLine(it._local_id, { vat_rate: parseFloat(e.target.value) })}
                            data-testid={`pv-free-vat-${it._local_id}`}
                            title="Mehrwertsteuersatz"
                            className="h-9 sm:h-7 bg-zinc-800 border border-zinc-700 rounded px-1 text-[11.5px]"
                          >
                            {VAT_OPTIONS.map((r) => (
                              <option key={r} value={String(r)}>{Math.round(r * 100)}% MwSt</option>
                            ))}
                          </select>
                          <span className="font-mono ml-auto sm:ml-0 sm:w-24 text-right">{_eur(lineGross)}</span>
                        </div>
                      );
                    }
                    const c = catalog.find((x) => x.id === it.component_id); if (!c) return null;
                    const vat = typeof it.vat_rate === "number" ? it.vat_rate : (DEFAULT_VAT_BY_TYPE[c.type] ?? 0);
                    const lineNet = cartUnitNet(it, c) * it.qty;
                    const lineGross = lineNet * (1 + vat);
                    return (
                      <div key={it.component_id} className="flex flex-wrap sm:flex-nowrap items-center gap-2 text-[12.5px] pb-3 sm:pb-0 border-b border-zinc-800/70 sm:border-0" data-testid={`pv-cart-line-${it.component_id}`}>
                        <span className="basis-[calc(100%-3rem)] sm:basis-auto flex-1 min-w-0 sm:truncate font-medium sm:font-normal leading-snug">{c.brand} {c.model}</span>
                        <button onClick={() => removeItem(it.component_id)} data-testid={`pv-remove-${it.component_id}`} className="sm:order-last w-10 h-10 sm:w-auto sm:h-auto inline-flex items-center justify-center rounded-lg sm:rounded-none bg-rose-500/10 sm:bg-transparent text-rose-300 hover:text-rose-200"><Trash2 size={15}/></button>
                        <input type="number" min="1" value={it.qty} onChange={(e) => setQty(it.component_id, parseInt(e.target.value, 10) || 1)}
                          className="w-14 h-9 sm:h-7 bg-zinc-800 border border-zinc-700 rounded px-2 text-center" data-testid={`pv-qty-${it.component_id}`}/>
                        <select
                          value={String(vat)}
                          onChange={(e) => setVat(it.component_id, parseFloat(e.target.value))}
                          data-testid={`pv-vat-${it.component_id}`}
                          title="Mehrwertsteuersatz für diese Position"
                          className="h-9 sm:h-7 bg-zinc-800 border border-zinc-700 rounded px-1 text-[11.5px]"
                        >
                          {VAT_OPTIONS.map((r) => (
                            <option key={r} value={String(r)}>{Math.round(r * 100)}% MwSt</option>
                          ))}
                        </select>
                        <span className="font-mono ml-auto sm:ml-0 sm:w-24 text-right">{_eur(lineGross)}</span>
                      </div>
                    );
                  })}
                  {/* Iter88zbg — Freie-Position-Button unterhalb der Zeilen */}
                  <div className="pt-1">
                    <button onClick={addFreeLine} data-testid="pv-add-free-line"
                      className="h-8 px-3 rounded-lg bg-[#0F0F11] border border-dashed border-zinc-500 text-zinc-300 hover:border-[#00E575] hover:text-[#00E575] text-[11.5px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5">
                      <Plus size={12}/> Freie Position
                    </button>
                  </div>
                  <RoofFitWarning modules={countModules(items, catalog)} limit={roofLimit} testid="pv-cart-roof-warn" onFit={fitCartToRoof} />
                  <InverterWarning items={items} catalog={catalog} testid="pv-cart-inverter-warn" />
                  <div className="pt-2 border-t border-zinc-800 space-y-0.5">
                    {/* Iter88zbh — Rabatt-Eingabe analog WP-Konfigurator */}
                    <div className="flex flex-wrap items-center gap-2 text-[12px] text-zinc-300 py-1" data-testid="pv-discount-row">
                      <span className="flex-1 font-semibold uppercase tracking-widest text-[11px]">Rabatt</span>
                      <div className="inline-flex rounded overflow-hidden border border-zinc-700">
                        <button type="button"
                          onClick={() => { setDiscountMode("pct"); setDiscountEur(0); }}
                          data-testid="pv-discount-mode-pct"
                          className={`h-7 px-2 text-[11.5px] font-bold ${discountMode === "pct" ? "bg-[#00E575] text-[#09090B]" : "bg-zinc-800 text-zinc-300"}`}>%</button>
                        <button type="button"
                          onClick={() => { setDiscountMode("eur"); setDiscountPct(0); }}
                          data-testid="pv-discount-mode-eur"
                          className={`h-7 px-2 text-[11.5px] font-bold ${discountMode === "eur" ? "bg-[#00E575] text-[#09090B]" : "bg-zinc-800 text-zinc-300"}`}>€</button>
                      </div>
                      {discountMode === "pct" ? (
                        <input type="number" min="0" max="100" step="0.5"
                          value={discountPct}
                          onChange={(e) => setDiscountPct(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                          data-testid="pv-discount-pct-input"
                          className="w-20 h-7 bg-zinc-800 border border-zinc-700 rounded px-2 text-right font-mono"
                          placeholder="0"
                        />
                      ) : (
                        <input type="number" min="0" step="10"
                          value={discountEur}
                          onChange={(e) => setDiscountEur(Math.max(0, parseFloat(e.target.value) || 0))}
                          data-testid="pv-discount-eur-input"
                          className="w-24 h-7 bg-zinc-800 border border-zinc-700 rounded px-2 text-right font-mono"
                          placeholder="0"
                        />
                      )}
                      <span className="font-mono min-w-[4rem] sm:w-24 text-right text-amber-300" data-testid="pv-discount-computed">
                        {liveTotals.discountEur > 0 ? `− ${_eur(liveTotals.discountEur)}` : ""}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[12px] text-zinc-400">
                      <span>Netto</span><span className="font-mono">{_eur(liveTotals.net)}</span>
                    </div>
                    {liveTotals.vatDelta > 0 && (
                      <div className="flex items-center justify-between text-[12px] text-zinc-400">
                        <span>MwSt (19% auf Wallbox &amp; sonstige)</span>
                        <span className="font-mono">{_eur(liveTotals.vatDelta)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[13px] font-bold">Gesamt: {liveTotals.kwp} kWp</span>
                      <span className="font-display text-xl font-bold text-[#00E575]" data-testid="pv-live-total">{_eur(liveTotals.price)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 pt-2">
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Konfigurations-Name"
                      className="basis-full sm:basis-auto flex-1 h-10 sm:h-9 bg-zinc-800 border border-zinc-700 rounded px-3 text-[13px]" data-testid="pv-config-name"/>
                    <label className="inline-flex items-center gap-2 text-[12px] font-medium h-10 sm:h-auto">
                      <input type="checkbox" checked={useBattery} onChange={(e) => setUseBattery(e.target.checked)} className="w-4 h-4" data-testid="pv-use-battery"/> mit Speicher
                    </label>
                    <button onClick={saveConfig} disabled={saving || editingOfferId} data-testid="pv-save-config"
                      className="flex-1 sm:flex-none h-11 sm:h-9 px-4 rounded-lg sm:rounded bg-[#00E575] text-[#09090B] font-bold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50"
                      title={editingOfferId ? "Beim Bearbeiten eines Angebots wird direkt ein neues Angebot erzeugt (siehe rechts)." : ""}>
                      {saving ? <Loader2 size={13} className="animate-spin"/> : <Package size={13}/>}
                      {editingConfigId ? "Aktualisieren" : "Als Konfig speichern"}
                    </button>
                    {editingOfferId && (
                      <button
                        onClick={saveAsNewPvOffer}
                        disabled={creatingOffer || items.length === 0}
                        data-testid="pv-save-as-new-offer"
                        title='Neues PV-Angebot mit neuer Nummer erzeugen. Das aktuell bearbeitete Angebot wird automatisch als „abgelöst" markiert.'
                        className="basis-full sm:basis-auto h-11 sm:h-9 px-4 rounded-lg sm:rounded bg-amber-500 text-zinc-900 font-bold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-amber-400 disabled:opacity-50"
                      >
                        {creatingOffer ? <Loader2 size={13} className="animate-spin"/> : <FileText size={13}/>}
                        Als neues Angebot speichern
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <RoofLayoutEditor projectId={projectId} catalog={catalog} savedConfigs={savedConfigs} pvOffers={pvOffers} onAdjusted={loadAll} />

        {/* Iter89c (2026-02-20) — 3a) Erzeugte PV-Angebote. Analog zur
            Angebots-Liste bei WP-Projekten. Jeder Eintrag hat einen
            Konfigurator-Button, der das Angebot zurück in den Konfigurator
            lädt — der Rep kann Positionen anpassen, ohne bei Null zu starten.
            Beim Speichern wird ein neues Angebot mit neuer Nummer erzeugt;
            das alte wird Backend-seitig als „abgelöst" markiert. */}
        {pvOffers.length > 0 && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-3" data-testid="pv-offers-list">
            <h2 className="font-display text-lg font-bold inline-flex items-center gap-2">
              <FileText size={18} className="text-[#0EA5E9]"/> PV-Angebote
              <span className="text-[11px] text-zinc-400 font-medium">({pvOffers.length})</span>
            </h2>
            <div className="space-y-2">
              {pvOffers.map((o, idx) => {
                const isLatest = idx === 0;
                const isSuperseded = o.status === "superseded";
                const createdLabel = o.created_at
                  ? new Date(o.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
                  : "";
                const isEditing = editingOfferId === o.id;
                const canEdit = o.status !== "signed" && o.status !== "cancelled";
                return (
                  <div
                    key={o.id}
                    className={`bg-[#0F0F11] border rounded-xl p-3 space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:gap-3 ${
                      isEditing ? "border-amber-400/60 bg-amber-500/5"
                      : isLatest ? "border-[#0EA5E9]/40"
                      : isSuperseded ? "border-red-500/30 opacity-70"
                      : "border-zinc-800"
                    }`}
                    data-testid={`pv-offer-${o.id}`}
                  >
                    <div className="sm:flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[14px] sm:text-[13px] font-bold text-[#0EA5E9]">{o.offer_number}</span>
                        {createdLabel && (
                          <span className="text-[11.5px] font-mono text-zinc-300">· {createdLabel}</span>
                        )}
                        {isLatest && pvOffers.length > 1 && (
                          <span className="px-1.5 py-0.5 rounded-md text-[9.5px] font-extrabold uppercase tracking-wider bg-[#0EA5E9]/15 text-[#0EA5E9]">Aktuell</span>
                        )}
                        {isSuperseded && (
                          <span className="px-1.5 py-0.5 rounded-md text-[9.5px] font-extrabold uppercase tracking-wider bg-red-500/20 text-red-300" data-testid={`pv-offer-superseded-${o.id}`}>
                            Abgelöst · gesperrt
                          </span>
                        )}
                        {o.signature && (
                          <span className="px-1.5 py-0.5 rounded-md text-[9.5px] font-extrabold uppercase tracking-wider bg-[#00E575]/15 text-[#00E575]">✓ Unterschrieben</span>
                        )}
                        {Number(o.discount_eur_applied) > 0 && (
                          <span className="px-1.5 py-0.5 rounded-md text-[9.5px] font-extrabold uppercase tracking-wider bg-amber-500/15 text-amber-300" title={`Rabatt: ${_eur(o.discount_eur_applied)}`}>
                            {Number(o.discount_percent_requested) > 0
                              ? `− ${Number(o.discount_percent_requested).toFixed(1).replace(".", ",")} %`
                              : `− ${_eur(o.discount_eur_applied)}`}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-zinc-300 mt-1 sm:mt-0.5 flex flex-wrap items-baseline gap-x-1.5">
                        <span className="font-mono text-zinc-100 font-bold text-[15px] sm:text-[12px]" data-testid={`pv-offer-total-${o.id}`}>{_eur(o.total_gross)}</span>
                        {o.total_kwp ? <span>· {Number(o.total_kwp).toFixed(2).replace(".", ",")} kWp</span> : null}
                        {(o.items?.length || 0) > 0 && (
                          <span className="text-zinc-500">· {o.items.length} Position{o.items.length === 1 ? "" : "en"}</span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center [&>*:only-child]:col-span-2">
                    {canEdit && (
                      <button
                        onClick={() => isEditing ? cancelEditing() : loadOfferIntoConfigurator(o)}
                        data-testid={`pv-offer-edit-${o.id}`}
                        className={`h-10 sm:h-8 px-3 rounded-lg sm:rounded font-bold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 border transition ${
                          isEditing
                            ? "bg-zinc-700 border-zinc-600 text-zinc-100 hover:bg-zinc-600"
                            : "bg-amber-500/15 border-amber-500/40 text-amber-200 hover:bg-amber-500/25"
                        }`}
                        title={isEditing ? "Bearbeitung abbrechen" : "Angebot im Konfigurator öffnen und Positionen anpassen. Beim Speichern entsteht ein neues Angebot; das aktuelle wird abgelöst."}
                      >
                        {isEditing ? (<><X size={12}/> Abbrechen</>) : (<><Edit3 size={12}/> Konfigurator</>)}
                      </button>
                    )}
                    {o.pdf_storage_path && (
                      <a
                        href={fileUrl(o.pdf_storage_path)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`pv-offer-pdf-${o.id}`}
                        className="h-10 sm:h-8 px-3 rounded-lg sm:rounded bg-zinc-800 border border-zinc-700 text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-zinc-700 text-zinc-100 font-semibold"
                        title="Angebots-PDF öffnen"
                      >
                        <Download size={13}/> PDF
                      </a>
                    )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* 3) Saved configs */}
        {savedConfigs.length > 0 && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-3" data-testid="pv-saved-configs">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><FileText size={18} className="text-sky-400"/> Gespeicherte Konfigurationen</h2>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              {savedConfigs.length >= 2 && (
                <button onClick={recalcAllYields} disabled={!!recalcingId} data-testid="pv-recalc-all-yields"
                  title={roofLimit?.has_location ? "Genauen Ertrag aller Konfigurationen neu berechnen" : "Erst KI-Dachanalyse oder Google Solar durchführen"}
                  className="flex-1 sm:flex-none h-10 sm:h-9 px-3 rounded-lg sm:rounded border border-sky-500/40 text-sky-300 font-bold text-[12px] sm:text-[11.5px] inline-flex items-center justify-center gap-1.5 hover:bg-sky-500/10 disabled:opacity-50">
                  <RefreshCw size={13} className={recalcingId === "all" ? "animate-spin" : ""}/> Alle Erträge nachrechnen
                </button>
              )}
              {compareSelected.length >= 2 && (
                <button onClick={downloadCompare} data-testid="pv-compare-download"
                  className="flex-1 sm:flex-none h-10 sm:h-9 px-3 rounded-lg sm:rounded bg-amber-500 text-zinc-900 font-bold text-[12px] sm:text-[11.5px] inline-flex items-center justify-center gap-1.5 hover:bg-amber-400">
                  <FileBarChart size={13}/> Vergleich ({compareSelected.length})
                </button>
              )}
              </div>
            </div>
            <div className="space-y-2">
              {savedConfigs.map((cfg) => (
                <div key={cfg.id} className="bg-[#0F0F11] border border-zinc-800 rounded-xl p-3 space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:gap-3" data-testid={`pv-saved-${cfg.id}`}>
                  <div className="flex items-start gap-3 sm:flex-1 min-w-0">
                  <input type="checkbox" checked={compareSelected.includes(cfg.id)} onChange={() => toggleCompare(cfg.id)}
                    className="shrink-0 mt-1 w-4 h-4" title="Zum Vergleich auswählen" data-testid={`pv-compare-check-${cfg.id}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[14px] sm:text-[13px] leading-snug flex flex-wrap items-center gap-x-2 gap-y-1 break-words">
                      {cfg.name}
                      {/* Iter88zbh — Rabatt-Badge, damit Rep den Rabatt-Status ohne Öffnen sieht. */}
                      {Number(cfg.discount_eur_applied) > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] font-semibold" data-testid={`pv-saved-discount-${cfg.id}`}
                          title={`Rabatt angewendet: ${_eur(cfg.discount_eur_applied)}`}>
                          {Number(cfg.discount_percent_requested) > 0
                            ? `− ${Number(cfg.discount_percent_requested).toFixed(1).replace(".", ",")} %`
                            : `− ${_eur(cfg.discount_eur_applied)}`}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-zinc-400">
                      <span className="text-[15px] sm:text-[12px] font-bold text-zinc-100" data-testid={`pv-saved-price-${cfg.id}`}>{_eur(cfg.total_price_eur)}</span>
                      <span>{cfg.total_kwp} kWp</span>
                      <span data-testid={`pv-saved-yield-${cfg.id}`}>{cfg.pvgis?.yearly_kwh ? `${_num(Math.round(cfg.pvgis.yearly_kwh))} kWh/J (PVGIS)` : "Ertrag: Ø-Schätzung"}</span>
                      <button type="button" onClick={() => recalcYield(cfg)} disabled={recalcingId === cfg.id} data-testid={`pv-recalc-yield-${cfg.id}`}
                        title={roofLimit?.has_location ? "Genauen Ertrag für den Standort neu berechnen" : "Erst KI-Dachanalyse oder Google Solar durchführen"}
                        className="h-7 px-2 rounded-md border border-sky-500/40 text-sky-300 text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-sky-500/10 disabled:opacity-50">
                        <RefreshCw size={11} className={recalcingId === cfg.id ? "animate-spin" : ""} /> Ertrag nachrechnen
                      </button>
                    </div>
                    {exceedsRoof(countModules(cfg.items, catalog), roofLimit) && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <RoofFitBadge modules={countModules(cfg.items, catalog)} limit={roofLimit} testid={`pv-saved-roof-warn-${cfg.id}`} />
                        <button type="button" onClick={() => fitSavedConfig(cfg)} disabled={fittingId === cfg.id} data-testid={`pv-saved-fit-roof-${cfg.id}`}
                          className="h-8 sm:h-7 px-2.5 rounded-md bg-amber-500 text-zinc-900 text-[11.5px] font-bold inline-flex items-center gap-1 hover:bg-amber-400 disabled:opacity-50">
                          {fittingId === cfg.id ? <Loader2 size={11} className="animate-spin" /> : <Scissors size={11} />} Ans Dach anpassen
                        </button>
                      </div>
                    )}
                    <div className="mt-1 empty:hidden"><InverterBadge items={cfg.items} catalog={catalog} testid={`pv-saved-inverter-warn-${cfg.id}`} /></div>
                  </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
                  <button onClick={() => createPvOffer(cfg)} data-testid={`pv-create-offer-${cfg.id}`}
                    className="col-span-2 h-11 sm:h-8 px-3 rounded-lg sm:rounded bg-[#0EA5E9] hover:bg-[#0284C7] text-white border border-[#0369A1] font-bold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
                    <FileText size={14}/> Als Angebot
                  </button>
                  <button onClick={() => loadIntoConfigurator(cfg)} data-testid={`pv-load-cfg-${cfg.id}`}
                    className="h-10 sm:h-8 px-3 rounded-lg sm:rounded bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/50 text-sky-200 font-bold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5">
                    <Edit3 size={13}/> Bearbeiten
                  </button>
                  <button onClick={() => downloadOffer(cfg.id, cfg.name)} data-testid={`pv-offer-dl-${cfg.id}`}
                    className="h-10 sm:h-8 px-3 rounded-lg sm:rounded bg-zinc-800 border border-zinc-700 text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-zinc-700">
                    <Download size={13}/> PDF
                  </button>
                  <button onClick={() => sendOffer(cfg.id, cfg.name)} data-testid={`pv-offer-send-${cfg.id}`}
                    className="col-span-2 h-11 sm:h-8 px-3 rounded-lg sm:rounded bg-[#00E575] text-[#09090B] font-bold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] active:scale-[0.98] transition-transform">
                    <Mail size={14}/> Per E-Mail senden
                  </button>
                  <button onClick={() => optimizeBattery(cfg.id)} data-testid={`pv-optimize-${cfg.id}`}
                    className="h-10 sm:h-8 px-3 rounded-lg sm:rounded bg-amber-500 text-zinc-900 font-bold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-amber-400">
                    <Wand2 size={13}/> Speicher
                  </button>
                  <button onClick={() => deleteConfig(cfg)} data-testid={`pv-delete-cfg-${cfg.id}`}
                    className="h-10 sm:h-8 px-3 rounded-lg sm:rounded bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-200 font-bold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5"
                    title="Konfiguration löschen">
                    <Trash2 size={13}/> Löschen
                  </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Iter58 — E-Mail-Versand-Historie (aggregiert über alle PV-Angebote) */}
        {emailSends.length > 0 && (
          <section
            className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-2"
            data-testid="pv-email-sends-history"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold inline-flex items-center gap-2">
                <Send size={18} className="text-[#00E575]" />
                Versand-Historie
                <span className="text-[11px] text-zinc-400 font-medium">({emailSends.length})</span>
              </h2>
              <button
                onClick={loadEmailSends}
                disabled={emailSendsLoading}
                data-testid="pv-email-sends-refresh"
                className="h-7 px-2 rounded bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-[11px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                title="Signatur-Status neu laden"
              >
                <Loader2 size={11} className={emailSendsLoading ? "animate-spin" : ""} />
                Aktualisieren
              </button>
            </div>
            <div className="space-y-1.5">
              {emailSends.slice(0, 10).map((s) => {
                const dt = s.sent_at ? new Date(s.sent_at) : null;
                const dateStr = dt
                  ? dt.toLocaleString("de-DE", {
                      day: "2-digit", month: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })
                  : "—";
                const ok = s.status === "sent";
                // Iter59 — Signatur-Widget: Live-Status je Angebot
                const st = s.offer_status;
                const signedAt = s.offer_signed_at ? new Date(s.offer_signed_at) : null;
                const signedStr = signedAt
                  ? signedAt.toLocaleString("de-DE", {
                      day: "2-digit", month: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })
                  : "";
                const sigBadge = (() => {
                  if (st === "signed") return {
                    icon: <CheckCircle2 size={12} className="text-[#00E575]" />,
                    text: `Signiert${signedStr ? ` am ${signedStr}` : ""}${s.signer_name ? ` · ${s.signer_name}` : ""}`,
                    cls: "text-[#00E575] bg-[#00E575]/10 border-[#00E575]/30",
                  };
                  if (st === "revoked") return {
                    icon: <XCircle size={12} className="text-red-400" />,
                    text: "Widerrufen",
                    cls: "text-red-300 bg-red-500/10 border-red-500/30",
                  };
                  if (s.is_superseded || st === "superseded") return {
                    icon: <AlertCircle size={12} className="text-amber-400" />,
                    text: "Durch neuere Version ersetzt",
                    cls: "text-amber-300 bg-amber-500/10 border-amber-500/30",
                  };
                  if (st && st !== "sent") return {
                    icon: <Clock size={12} className="text-zinc-400" />,
                    text: "Warten auf Signatur",
                    cls: "text-zinc-300 bg-zinc-800 border-zinc-700",
                  };
                  return null;
                })();
                return (
                  <div
                    key={s.id}
                    className="bg-[#0F0F11] border border-zinc-800 rounded-lg px-3 py-2 text-[12.5px]"
                    data-testid={`pv-email-send-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                            ok ? "bg-[#00E575]" : "bg-red-500"
                          }`}
                          title={ok ? "Erfolgreich gesendet" : "Versand fehlgeschlagen"}
                        />
                        <span className="font-mono text-zinc-300 shrink-0">{s.offer_number || "—"}</span>
                        <span className="text-zinc-500">·</span>
                        <span className="text-zinc-100 truncate" title={s.to}>{s.to}</span>
                        {s.has_custom_message && (
                          <span className="text-[10px] text-sky-300 bg-sky-500/10 border border-sky-500/30 px-1.5 py-0.5 rounded shrink-0">
                            mit Nachricht
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11.5px] shrink-0">
                        <span className="text-zinc-400 hidden sm:inline">{s.sent_by_name}</span>
                        <span className="text-zinc-500 hidden sm:inline">·</span>
                        <Clock size={11} className="text-zinc-500" />
                        <span className="text-zinc-300 font-mono">{dateStr}</span>
                      </div>
                    </div>
                    {sigBadge && (
                      <div className="flex items-center gap-2 mt-1.5 pt-1.5 border-t border-zinc-800/60">
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${sigBadge.cls}`}
                          data-testid={`pv-email-send-sig-${s.id}`}
                        >
                          {sigBadge.icon}
                          {sigBadge.text}
                        </span>
                        {st && st !== "signed" && st !== "revoked" && st !== "superseded" && !s.is_superseded && s.public_token && (
                          <a
                            href={`/sign/${s.public_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-sky-300 hover:text-sky-200 inline-flex items-center gap-1"
                            data-testid={`pv-email-send-open-${s.id}`}
                          >
                            <ExternalLink size={10} /> Sign-Link öffnen
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {emailSends.length > 10 && (
                <div className="text-[11px] text-zinc-500 text-center pt-1">
                  … und {emailSends.length - 10} weitere Einträge
                </div>
              )}
            </div>
          </section>
        )}

        {/* Battery optimization result */}
        {batteryRec && (
          <section className="bg-amber-500/10 border border-amber-500/40 rounded-2xl p-5 space-y-3" data-testid="pv-battery-rec">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><Wand2 size={18} className="text-amber-300"/> Speicher-Optimierung</h2>
              <button onClick={() => setBatteryRec(null)} className="text-zinc-400 hover:text-zinc-200 text-sm">Schließen</button>
            </div>
            <div className="text-[13px] text-zinc-100" dangerouslySetInnerHTML={{__html: batteryRec.narrative}} />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] font-medium">
                <thead><tr className="text-[12px] uppercase text-zinc-300 border-b border-zinc-700">
                  <th className="text-left py-1.5">Modell</th><th className="text-right">kWh</th><th className="text-right">Preis</th>
                  <th className="text-right">Eigenverbr.</th><th className="text-right">Ersparnis p.a.</th><th className="text-right">Amort.</th>
                </tr></thead>
                <tbody>
                  {batteryRec.candidates.slice(0, 6).map((c, i) => (
                    <tr key={i} className={`border-b border-zinc-800/50 ${c.kwh === batteryRec.optimal_kwh ? "bg-[#00E575]/10" : ""}`}>
                      <td className="py-1.5">{c.brand} {c.model}</td>
                      <td className="text-right font-mono">{c.kwh}</td>
                      <td className="text-right font-mono">{_eur(c.battery_price_eur)}</td>
                      <td className="text-right font-mono">{c.self_consumption_pct} %</td>
                      <td className="text-right font-mono text-[#00E575]">{_eur(c.yearly_savings_eur)}</td>
                      <td className="text-right font-mono">{c.payback_years ? `${c.payback_years} J` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* 4) Combo-Wirtschaftlichkeit */}
        {combo && combo.combo && (
          <section className="bg-gradient-to-br from-[#00E575]/10 to-amber-500/10 border border-[#00E575]/40 rounded-2xl p-5" data-testid="pv-combo">
            <h2 className="font-display text-lg font-bold mb-2">WP + PV Cross-Sell</h2>
            <p className="text-[12.5px] text-zinc-200 mb-3">{combo.combo.narrative}</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="text-[12px] uppercase text-zinc-300 font-bold">Gesamt-Investition</div><div className="font-display text-lg font-bold">{_eur(combo.combo.total_invest_eur)}</div></div>
              <div><div className="text-[12px] uppercase text-zinc-300 font-bold">Ersparnis p.a.</div><div className="font-display text-lg font-bold text-[#00E575]">{_eur(combo.combo.total_yearly_savings_eur)}</div></div>
              <div><div className="text-[12px] uppercase text-zinc-300 font-bold">PV→WP-Bonus p.a.</div><div className="font-display text-lg font-bold text-amber-300">{_eur(combo.combo.extra_yearly_savings_eur)}</div></div>
            </div>
          </section>
        )}
      </main>

      {/* Iter58 — E-Mail-Vorschau-Dialog (statt Browser-prompt/confirm) */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => !o && closeEmailDialog()}>
        <DialogContent
          className="ios-no-zoom bg-[#18181B] border-zinc-800 text-zinc-100 max-w-lg w-[calc(100%-1rem)] sm:w-full max-h-[92dvh] overflow-y-auto rounded-xl p-4 sm:p-6"
          data-testid="pv-email-dialog"
        >
          <DialogHeader className="pr-6 text-left">
            <DialogTitle className="font-display text-base sm:text-lg font-bold inline-flex items-center gap-2">
              <Mail size={18} className="text-[#00E575]" />
              PV-Angebot per E-Mail senden
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-zinc-400 leading-relaxed">
              Es wird ein rechtsverbindliches Angebot aus &bdquo;<b className="text-zinc-200">{emailDialog.cfgName}</b>&ldquo; erzeugt
              (AGB, Widerrufsbelehrung, 0 % USt § 12 Abs. 3 UStG) und mit digitalem
              Signing-Link per E-Mail versendet. Das PDF wird als Anhang mitgeschickt.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1">
                Empfänger-E-Mail
              </label>
              <input
                type="email"
                value={emailDialog.to}
                onChange={(e) => setEmailDialog((d) => ({ ...d, to: e.target.value }))}
                placeholder="kunde@beispiel.de"
                disabled={emailDialog.sending}
                data-testid="pv-email-dialog-to"
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded px-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none disabled:opacity-60"
              />
              {customerEmail && emailDialog.to !== customerEmail && (
                <button
                  type="button"
                  onClick={() => setEmailDialog((d) => ({ ...d, to: customerEmail }))}
                  className="text-[11px] text-sky-300 hover:text-sky-200 mt-1"
                  data-testid="pv-email-dialog-use-customer"
                >
                  → Kunden-E-Mail übernehmen ({customerEmail})
                </button>
              )}
            </div>

            <TeamCopyNote projectId={projectId} />
            {/* Iter88s: CC-Empfänger (vorbelegt mit Sachbearbeiter, mit ";" mehrere)
                Iter88zbs: Autofill-Historie (LocalStorage + Backend). */}
            <div>
              <label className="block text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1">
                CC (optional)
              </label>
              <AutofillInput
                type="text"
                value={emailDialog.cc}
                onChange={(v) => setEmailDialog((d) => ({ ...d, cc: v }))}
                placeholder="sachbearbeiter@..; buchhaltung@.."
                disabled={emailDialog.sending}
                dataTestid="pv-email-dialog-cc"
                storageKey="cc_email"
                autoComplete="email"
                splitOn={[";", ","]}
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded px-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none disabled:opacity-60"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                {customerSachbearbeiterEmail
                  ? `Vorbelegt mit Sachbearbeiter „${customerSachbearbeiterName || customerSachbearbeiterEmail}". Mehrere Adressen mit „;" trennen.`
                  : `Optional. Mehrere Adressen mit „;" oder „," trennen.`}
              </p>
            </div>

            <div>
              <label className="block text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1">
                Textbaustein
              </label>
              <select
                value={emailDialog.templateId}
                onChange={(e) => applyTemplate(e.target.value)}
                disabled={emailDialog.sending}
                data-testid="pv-email-dialog-template"
                className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded px-3 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none disabled:opacity-60"
              >
                {emailTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-1">
                {(emailTemplates.find((t) => t.id === emailDialog.templateId)?.description) || ""}
              </p>
            </div>

            <div>
              <label className="block text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1">
                Persönliche Nachricht (optional)
              </label>
              <textarea
                value={emailDialog.message}
                onChange={(e) => setEmailDialog((d) => ({ ...d, message: e.target.value }))}
                placeholder="Sehr geehrter Herr Müller, wie besprochen im Termin am 10.06. …"
                rows={3}
                disabled={emailDialog.sending}
                data-testid="pv-email-dialog-message"
                className="w-full bg-[#09090B] border border-zinc-700 rounded px-3 py-2 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none disabled:opacity-60 resize-none"
              />
              <p className="text-[11px] text-zinc-500 mt-1 italic">
                Erscheint als Einleitung über dem Angebots-Zusammenfassungstext.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sticky -bottom-4 sm:static -mx-4 sm:mx-0 px-4 sm:px-0 pt-3 pb-4 sm:p-0 bg-[#18181B] border-t border-zinc-800 sm:border-0">
            <button
              onClick={closeEmailDialog}
              disabled={emailDialog.sending}
              data-testid="pv-email-dialog-cancel"
              className="h-11 sm:h-9 px-3 rounded-lg sm:rounded bg-zinc-800 border border-zinc-700 text-zinc-200 font-semibold text-[13px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-zinc-700 disabled:opacity-40"
            >
              <X size={14} /> Abbrechen
            </button>
            <button
              onClick={submitEmailDialog}
              disabled={emailDialog.sending || !emailDialog.to.trim()}
              data-testid="pv-email-dialog-submit"
              className="h-12 sm:h-9 px-4 rounded-lg sm:rounded bg-[#00E575] text-[#09090B] font-bold text-[14px] sm:text-[12px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {emailDialog.sending
                ? <><Loader2 size={14} className="animate-spin" /> Wird gesendet…</>
                : <><Send size={14} /> Erstellen &amp; Senden</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
